import type { TemplateResult } from 'lit'
import type { Question, QuestionType, SelectionLimit } from '../contract'
import type { AnswerGap, Step } from '../state'
import type { DropdownHandlers, DropdownState } from './dropdown'
import { css, html, nothing } from 'lit'
import { answerFormatHint, precisionInput } from '../question-precision'
import { isMultiSelect, rendersOtherInput, resolveSelectionLimit, selectionCap } from '../question-type'
import { answerGap } from '../state'
import { dropdownView } from './dropdown'
import { iconOption } from './icons'

// View-model + callbacks for the pure question renderers (the element owns the state).
// 展開中的 dropdown 狀態與其開關 handler 定義在 dropdown.ts，這裡直接沿用 —— 同一組欄位
// 不在兩個檔案各寫一份。
export interface QuestionState extends DropdownState {
  textAnswers: Record<string, string>
  // 按下推進鈕時未通過驗證的題目 id。缺省（如 admin 預覽的一次性渲染）= 沒有任何題目被標記。
  invalidQIds?: string[]
  // 這一份畫面只是拿來量高度（inline 作答區的撐高層，見 active.ts 的 .answer-sizer）。開啟時不輸出
  // 任何會跟 live 那份撞到的識別：標題 id、指向它的 aria-labelledby、dropdown 的 data-dd-trigger 與
  // aria-controls。其餘必須與一般畫面逐字相同，否則量出來的高度對不上真正作答的那一份。
  // 只有 true 與「不帶」兩種值：不帶 = 一般畫面，呼叫端不必（也不該）寫 sizing: false。
  sizing?: true
}
export interface QuestionHandlers extends DropdownHandlers {
  setText: (qId: string, value: string) => void
}

// Question-body styles (composed into the element's Shadow DOM static styles). The
// frame chrome, step counter, and nav footer styles live in survey-element.ts.
export const questionStyles = css`
  /* 題目標題：mobile Type/Title/M（16/600，Figma 313:2895）、desktop Type/Title/L
     （20/600/ls 1.2，Figma 166:2426）。 */
  .q-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
  }
  @container frame (min-width: 640px) {
    .q-title {
      font-size: 20px;
      letter-spacing: 1.2px;
    }
  }
  /* 題型提示（可複選…）：14px 灰，inline 於標題尾（Figma 280:2382）。 */
  .q-note {
    margin-left: 4px;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.5px;
    color: var(--mp-text-meta);
  }
  /* 選項採 Figma 的扁平列表式（非卡片框）：每列 = 24px icon 指示器 + 18px 文字，
     底部一條 divider 線（縮排至文字起點）。選中時文字轉品牌色。 */
  /* 標題到答案區的間距 mobile 4（Figma 313:2891：Options Container y=27 − Question Header 高 23）、
     desktop 8（Figma 280:2377：Options Container y=38 − Question Container 高 30）。四種題型的
     答案區（.opts / .line-input / .dropdown-trigger / .scale-row）共用這組數字。
     稿上 inline 的手機版（303:2553）這一段是 8 而非 4，與 overlay 手機版對不起來；差 4px 不
     另設一組覆寫，兩個手機版型一律取 4。 */
  .opts {
    display: grid;
    grid-template-columns: 1fr;
    /* 欄間距 mobile 4（Figma 313:3124）、desktop 8（Figma 72:204）。 */
    column-gap: 4px;
    margin-top: 4px;
  }
  /* 預設單欄（Figma Desktop 示範）。兩欄要由題目資料指定（is_two_column 為 true，
     Figma Type=2 Column variant 280:2665），且容器夠寬才啟用。門檻 340px：mobile inline
     （容器 402、q-body ~350）在 Figma 298:2436 明確示範雙欄，要放行；sidebar 360
     （q-body ~300）維持單欄。 */
  @container (min-width: 340px) {
    .opts[data-columns='2'] { grid-template-columns: 1fr 1fr; }
    /* 最後一列不畫線：倒數第二個落在第一欄（奇數位）⟺ 選項共偶數個，此時它與
       :last-child 同列。留在 container query 內，退回單欄時這條線要保留。 */
    .opts[data-columns='2'] .opt:nth-last-child(2):nth-child(odd)::after {
      content: none;
    }
  }
  /* 選項列尺寸：mobile 16px 字/16px icon/gap 4/py8 + 44px 列高鎖（min-height，Figma
     313:2994 的 min/max-h 44）；desktop 18px 字/24px icon/gap 8/py12、不鎖高
     （Figma 288:1940）。divider 縮排至標籤起點。 */
  .opt {
    position: relative;
    display: flex;
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
    border: none;
    background: none;
    font: inherit;
    color: var(--mp-text-secondary);
    text-align: left;
    cursor: pointer;
  }
  /* 只畫在選項之間：最後一個選項下方沒有線（Figma 280:2525）。 */
  .opt::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 20px;
    height: 1px;
    background: var(--mp-border-sub);
  }
  .opt:last-child::after {
    content: none;
  }
  .opt:hover {
    color: var(--mp-text-primary);
  }
  /* 選中態走 data-checked 而非 aria-checked：「其他」選項那一列的 .opt 是 wrapper div，
     aria-checked 掛在它內層真正可點的 .opt-toggle 上（見下方），只靠 aria 屬性選不到外框。
     aria-checked 仍然照常輸出，只是不再兼任樣式鉤子。 */
  .opt[data-checked='true'] {
    color: var(--mp-brand);
  }
  /* 複選達上限：未選中的選項禁用（不在 Figma 規格內，沿用既有 placeholder 灰階表示不可互動）。 */
  .opt:disabled,
  .opt[data-disabled='true'] {
    color: var(--mp-text-placeholder);
    cursor: not-allowed;
  }
  .opt:disabled .opt-ind,
  .opt[data-disabled='true'] .opt-ind {
    color: var(--mp-text-placeholder);
  }
  .opt-ind {
    flex: none;
    width: 16px;
    height: 16px;
    /* Figma Icon-Option 用 icon/secondary（#4d4d4d），非 border 灰。 */
    color: var(--mp-text-secondary);
  }
  .opt[data-checked='true'] .opt-ind {
    color: var(--mp-brand);
  }
  .opt-text {
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.5px;
  }
  /* 「其他」選項（Figma Type=Radio-其他 / Checkbox-其他，166:4680）：指示器 + 文字佔內容寬，
     輸入框吃掉該列剩下的寬度（設計稿 desktop：文字結束於 x=69、輸入框 x=77 起算到列尾 360）。
     輸入框不能包在 .opt 按鈕裡 —— <button> 內不得有可聚焦子孫，ARIA 也禁止 radio/checkbox 含
     互動元素。所以這一列的 .opt 是 wrapper div，可點的部分縮成內層的 .opt-toggle。 */
  .opt--other {
    gap: 8px;
  }
  .opt-toggle {
    display: flex;
    flex: none;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: inherit;
  }
  /* 沿用文字題 .line-input 的底線語彙，但去掉它為獨立欄位而設的 margin/min-height —— 這裡是
     嵌在選項列裡、由該列決定高度。 */
  .opt-other-input {
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    /* 填滿列高而不是撐開它：設計稿裡這一列與一般選項列同高（都是 50），輸入框是 y=0 h=50
       跨滿整列（166:4715）。交給輸入框自己的 padding 去撐，列會比隔壁的選項列高一截。 */
    align-self: stretch;
    padding: 0 4px;
    border: none;
    border-bottom: 1px solid var(--mp-border-default);
    background: none;
    color: var(--mp-text-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 500;
    /* ⚠️ 必須寫在上面的 font: inherit 之後 —— 那是 shorthand，會把 line-height 一併重設。
       壓到 1 是為了讓輸入框的「假設高度」不超過同列的選項文字：flex 行高取所有項目假設高度的
       最大值，即使項目是 stretch。不壓的話這一列會比隔壁的選項列高 1px、底部分隔線跟著錯開。
       單行 text input 在框高於行高時會自己把文字置中，所以壓行高不影響外觀。 */
    line-height: 1;
    letter-spacing: 0.5px;
    outline: none;
  }
  .opt-other-input::placeholder {
    color: var(--mp-text-placeholder);
  }
  .opt-other-input:focus {
    border-bottom: 1.5px solid var(--mp-brand-weak);
  }
  @container frame (min-width: 640px) {
    .opts {
      column-gap: 8px;
      margin-top: 8px;
    }
    .opt {
      gap: 8px;
      min-height: 0;
      padding: 12px 0;
    }
    .opt::after {
      left: 32px;
    }
    .opt-ind {
      width: 24px;
      height: 24px;
    }
    .opt-text {
      font-size: 18px;
      font-weight: 400;
    }
    .opt-toggle {
      gap: 8px;
    }
    .opt-other-input {
      font-size: 18px;
      font-weight: 400;
    }
  }
  /* Figma 單行 input（TEXT 280:2734 / DATE 共用）：底線式（bottom-border only）、
     placeholder = text/placeholder、focus = border/focus（brand-weak）1.5px 底線。
     mobile（Figma 313:3496）：16px Medium、py8、44px 列高；desktop：18px Regular、py12。 */
  .line-input {
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    border: none;
    border-bottom: 1px solid var(--mp-border-default);
    background: var(--mp-bg-base);
    color: var(--mp-text-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.5px;
    outline: none;
    margin-top: 4px;
    padding: 8px 4px;
  }
  /* 日期欄不吃滿寬：Figma 固定 mobile 200（166:2849）/ desktop 220（154:726）。用 max-width
     而非 width，窄容器（sidebar 的 q-body 僅 240）才不會溢出。 */
  .line-input[type='date'] {
    max-width: 200px;
  }
  /* 原生 date input 的日曆指示器對齊 Figma 尺寸（mobile 20 / desktop 24；僅 Chromium 有此偽元素，
     其他引擎維持原生外觀）。 */
  .line-input::-webkit-calendar-picker-indicator {
    width: 20px;
    height: 20px;
  }
  .line-input::placeholder {
    color: var(--mp-text-placeholder);
  }
  .line-input:focus {
    border-bottom: 1.5px solid var(--mp-brand-weak);
  }
  /* 兩個底線式輸入框自己畫 focus（底線轉 brand 色，Figma State=Focused 50:33），不再疊
     host-styles.ts 那圈通用的 2px focus ring —— 設計稿的 Focused 只有底線變色，外面再套一圈
     就成了「整個框起來」。自繪 dropdown 的觸發框早就是這樣處理（dropdown.ts 的 :focus 那條）。
     ⚠️ 這裡帶 :focus-visible 是為了把 specificity 墊到 (0,2,0)：base 的 outline:none 只有
     (0,1,0)，與 host-styles 的裸 :focus-visible 同分，而 questionStyles 排在 hostStyles
     之前（survey-element.ts 的 styles 陣列），同分會輸給順序。 */
  .line-input:focus-visible,
  .opt-other-input:focus-visible {
    outline: none;
  }
  /* forced-colors（Windows 高對比）把 box-shadow 整個丟掉、border-color 一律換成系統色，
     所以上面那兩種替代指示 —— 一般態的「底線轉 brand」與 error 態的 box-shadow —— 在這個
     模式下都不會呈現。outline 是唯一會被保留（且由系統換成 Highlight 色）的屬性，所以在
     這裡把它還回去；不還的話，高對比使用者連一個焦點指示都沒有。
     Safari 沒有這個模式，media query 永不匹配，對 baseline 其他引擎無副作用。 */
  @media (forced-colors: active) {
    .line-input:focus-visible,
    .opt-other-input:focus-visible {
      outline: 2px solid;
      outline-offset: 2px;
    }
  }
  @container frame (min-width: 640px) {
    .line-input {
      min-height: 0;
      margin-top: 8px;
      padding: 12px 4px;
      font-size: 18px;
      font-weight: 400;
    }
    .line-input[type='date'] {
      max-width: 220px;
    }
    .line-input::-webkit-calendar-picker-indicator {
      width: 24px;
      height: 24px;
    }
  }
  /* Figma 量表（desktop 288:2220 / mobile 313:3242）：每點 = 圓形數字鈕 + 標籤垂直堆疊、
     整列 space-between；選中 = 品牌底白字、標籤轉品牌色。
     mobile：鈕 32px、字 14 Regular；desktop：鈕 40px、字 16 Medium。 */
  .scale-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
  }
  .scale-item {
    display: flex;
    /* min-width:0 讓 flex 子項可縮到內容寬以下，長標籤在自己這欄內折行、不把整列撐寬（每點都有標籤後才需要）。 */
    flex: 1;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .scale-btn {
    display: flex;
    flex: none;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--mp-border-default);
    border-radius: 9999px;
    background: var(--mp-bg-base);
    color: var(--mp-text-secondary);
    font: inherit;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
  }
  .scale-btn[aria-checked='true'] {
    border-color: var(--mp-brand);
    background: var(--mp-brand);
    color: var(--mp-text-inverse);
  }
  /* 標籤寬度綁定該點所分到的欄寬（max-width:100%），相鄰標籤之間至少隔一個 scale-row gap，不會擠在一起；
     超過欄寬就折行，overflow-wrap 讓無斷點的長字（英數/網址）也能斷。每點皆有標籤，故不能放任 label 無限變寬。 */
  .scale-label {
    max-width: 100%;
    overflow-wrap: break-word;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: 0.5px;
    text-align: center;
    color: var(--mp-text-secondary);
  }
  .scale-item[data-checked='true'] .scale-label {
    color: var(--mp-brand);
  }
  @container frame (min-width: 640px) {
    .scale-row {
      margin-top: 8px;
    }
    .scale-btn {
      width: 40px;
      height: 40px;
      font-size: 16px;
      font-weight: 500;
    }
    .scale-label {
      font-size: 16px;
      font-weight: 500;
    }
  }
  /* 必填題標紅星（業界慣例）；選填不另標示（PM 決策）。 */
  .q-required {
    margin-left: 4px;
    color: var(--mp-alert);
    font-weight: 600;
  }
  /* 驗證未通過的欄位（Figma State=Error 文字框 50:30）：底線轉 alert 色。下拉的同名狀態
     在 dropdown.ts。:focus 那條也要覆寫 —— 底線 shorthand 的 specificity（0,2,1）高於單獨的 .is-error。 */
  .line-input.is-error,
  .line-input.is-error:focus {
    border-bottom-color: var(--mp-alert);
  }
  /* Error 態的焦點指示。非 error 態靠「灰 1px → brand 1.5px」被看見，而上面那條把顏色釘死成
     alert，只剩線寬的 0.5px 差 —— 在 DPR=1 會被捨入到同一個像素。取消通用 focus ring 之後
     （見上面的 :focus-visible 那條），送出後標紅的欄位會變成按 Tab 也看不出焦點停在哪一個。
     設計稿沒有 Error×Focused 這一格（50:33 的五個狀態互斥），所以這是補的，不是照抄的。
     用 box-shadow 在底線正下方補一條，而不是把 border 加粗：border 會進 layout，實測欄位
     從 52 長到 53、下面那行錯誤說明跟著位移 1px，鍵盤連續巡覽多個紅欄位就一路抖。
     box-shadow 不參與 layout，視覺上一樣是 2px 粗的紅線。 */
  .line-input.is-error:focus-visible {
    box-shadow: 0 1px 0 var(--mp-alert);
  }
  /* 缺什麼的說明文字，緊接在標題之下、答案區之上。選項題與量表題在 Figma 沒有欄位層級的 error 態，
     只靠這一行提示；Figma 也沒有任何題目層錯誤的位置（文字框 Error 態 50:30 只有紅底線、沒有說明文字），
     所以位置與間距都是補的、不是 Figma 值：與標題隔 4px，與答案區的間距沿用答案區自己的
     margin-top（mobile 4／desktop 8，見上方），出現說明時標題到答案區的距離只多出這一行的高度。
     <p> 沒有 reset，margin 一律寫全 —— 漏了 bottom 會吃到 UA 預設的 1em，疊進標題到答案的距離。 */
  .q-error {
    margin: 4px 0 0;
    font-size: 12px;
    letter-spacing: 0.5px;
    color: var(--mp-alert);
  }
`

// 穩定的題目標題 id（SDK-A11y，review 2026-07-10）：由 q.id 衍生，同一題重複 render id 不變、
// 不同題不相撞（Shadow DOM 本身即隔離 id 空間，這裡只需組內唯一）。目前只有 TEXT/DATE input
// 會透過 aria-labelledby 指到這個 id；其餘題型已用 aria-label 直接標名，維持原狀不動。
// q.id 型別為 string，但 consumer（如 admin live-preview）實務上會餵數字 id；SDK 其餘路徑
// 一律 String(q.id) 相容，這裡同樣先轉字串再處理，避免對數字呼叫 .replace 而 crash。
// aria-labelledby / aria-describedby 以空白分隔多個 id token；q.id 若含空白會被誤判成指向
// 多個元素，故把空白正規化為連字號。
function qIdToken(q: Question): string {
  return String(q.id).replace(/\s+/g, '-')
}
function qTitleId(q: Question): string {
  return `q-title-${qIdToken(q)}`
}
function qErrorId(q: Question): string {
  return `q-error-${qIdToken(q)}`
}

// 標題 id 與指向它的 aria-labelledby 是同一個識別的兩端：sizing 畫面兩端一起不出，
// 否則 ghost 的輸入框會指向 live 的標題（或哪天變成指向 ghost 自己的標題）。
function titleIdAttr(q: Question, state: QuestionState): string | typeof nothing {
  return state.sizing === true ? nothing : qTitleId(q)
}

// 必填題在標題尾標紅星（業界慣例）；選填不另標示（PM 決策：不顯示「選填」字樣）。
function qTitle(q: Question, state: QuestionState, note?: TemplateResult): TemplateResult {
  return html`<h2 class="q-title" id=${titleIdAttr(q, state)}>${q.text}${q.is_required ? html`<span class="q-required" aria-hidden="true">*</span>` : nothing}${note ?? nothing}</h2>`
}

// WAI-ARIA radio group keyboard model: arrow keys move focus between radios AND select,
// the companion to roving tabindex (only one radio is tab-reachable). Space/Enter selection
// is handled natively by <button>. Left/Up = previous, Right/Down = next, wrapping around.
// Applies to single-choice + scale (radiogroup); NOT to checkbox groups (each stays tabbable).
function onRadioGroupKeydown(e: KeyboardEvent, qKey: string, handlers: QuestionHandlers): void {
  const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown'
  const backward = e.key === 'ArrowLeft' || e.key === 'ArrowUp'
  if (!forward && !backward)
    return
  e.preventDefault()
  const group = e.currentTarget as HTMLElement
  const radios = Array.from(group.querySelectorAll<HTMLElement>('[role="radio"]'))
  const from = radios.indexOf(e.target as HTMLElement)
  if (from === -1)
    return
  const next = radios[(from + (forward ? 1 : -1) + radios.length) % radios.length]
  const optId = next?.dataset.optId
  if (!next || !optId)
    return
  next.focus()
  handlers.toggleOption(qKey, optId, false)
}

// Roving tabindex: only the selected radio is tab-reachable; if none is selected the first
// one is, so the group is always entered with a single Tab. Others get -1 and are reached by
// arrow keys. Checkbox groups don't rove — each checkbox stays in the tab order.
function rovingTabindex(selected: boolean, isFirst: boolean, anySelected: boolean): 0 | -1 {
  return selected || (!anySelected && isFirst) ? 0 : -1
}

// 複選題的標題註記文字，依選取數量限制與目前是否已勾滿（atCap，只有 at_most／exactly 會有）切換：
//   不限            「可複選」
//   at_most         「可複選（最多可選 N 項）」，N=1 時「最多可選 1 項」；勾滿「已達上限（最多 N 項）」
//   at_least        「可複選（至少選 N 項）」（沒有勾滿的狀態）
//   exactly         「可複選（需選 N 項）」，N=1 時「需選 1 項」；勾滿「已選滿 N 項」
// 勾滿且後台填了自訂訊息時改顯示該訊息：at_most／exactly 勾滿後不可能再超選，不會走到題目層的
// 錯誤狀態，自訂訊息只有在這裡才有機會被讀者看到。N=1 不加「可複選」前綴：與「只能選 1 項」語意矛盾。
function multiNoteText(limit: SelectionLimit | null, atCap: boolean): string {
  if (limit === null)
    return '可複選'
  if (atCap && limit.message !== undefined)
    return limit.message
  const n = limit.count
  switch (limit.mode) {
    case 'at_least':
      return `可複選（至少選 ${n} 項）`
    case 'at_most':
      if (atCap)
        return `已達上限（最多 ${n} 項）`
      return n === 1 ? `最多可選 ${n} 項` : `可複選（最多可選 ${n} 項）`
    case 'exactly':
      if (atCap)
        return `已選滿 ${n} 項`
      return n === 1 ? `需選 ${n} 項` : `可複選（需選 ${n} 項）`
  }
}

function multiNote(limit: SelectionLimit | null, atCap: boolean): TemplateResult {
  return html`<span class="q-note">${multiNoteText(limit, atCap)}</span>`
}

function optionQuestion(q: Question, state: QuestionState, handlers: QuestionHandlers, invalid: boolean): QuestionParts {
  const multi = isMultiSelect(q.type)
  const qKey = String(q.id)
  const picked = state.optionAnswers[qKey] ?? []
  const anySelected = picked.length > 0
  const limit = resolveSelectionLimit(q)
  const cap = selectionCap(limit)
  const atCap = cap !== null && picked.length >= cap
  const answer = html`
    <div class="opts-scroll">
      <div
        class="opts"
        data-columns=${q.is_two_column === true ? '2' : '1'}
        role=${multi ? 'group' : 'radiogroup'}
        aria-label=${q.text}
        aria-required=${q.is_required ? 'true' : nothing}
        aria-invalid=${invalid ? 'true' : nothing}
        aria-describedby=${invalid ? qErrorId(q) : nothing}
        @keydown=${multi ? undefined : (e: KeyboardEvent) => onRadioGroupKeydown(e, qKey, handlers)}
      >
        ${(q.options ?? []).map((o, i) => {
          const oKey = String(o.seq)
          const selected = picked.includes(oKey)
          // 已達上限（at_most／exactly 勾到 count）時，未選中的選項禁用
          // （已選中的仍可點擊取消——選中狀態不受 disabled 影響）。
          const disabled = atCap && !selected
          const role = multi ? 'checkbox' : 'radio'
          const tabIndex = multi ? nothing : rovingTabindex(selected, i === 0, anySelected)
          const optId = multi ? nothing : oKey
          // 「其他」選項：可點的部分縮成內層 .opt-toggle，讓輸入框能當它的兄弟節點存在
          // （<button> 內不得有可聚焦子孫）。選中時才長出輸入框（Figma Select=False 沒有它）。
          // rendersOtherInput 這個判斷目前恆真：這個函式只服務走 .opts 版型的題型（single/multiple），
          // 讀它而不是只看 o.is_other，是讓「誰會渲染輸入框」有一個可查的來源，不是靠「剛好走到
          // 這條渲染路徑」隱含決定（見 question-type.ts）。
          if (o.is_other && rendersOtherInput(q)) {
            return html`
              <div class="opt opt--other" data-checked=${selected ? 'true' : nothing} data-disabled=${disabled ? 'true' : nothing}>
                <button
                  type="button"
                  class="opt-toggle"
                  role=${role}
                  aria-checked=${selected}
                  aria-disabled=${disabled ? 'true' : nothing}
                  ?disabled=${disabled}
                  tabindex=${tabIndex}
                  data-opt-id=${optId}
                  @click=${() => handlers.toggleOption(qKey, oKey, multi)}
                >
                  ${iconOption(multi, selected)}
                  <span class="opt-text">${o.content}</span>
                </button>
                ${selected
                  ? html`<input
                      class="opt-other-input"
                      type="text"
                      maxlength="500"
                      .value=${state.textAnswers[qKey] ?? ''}
                      aria-label=${`${o.content}（請說明）`}
                      placeholder="請說明"
                      @input=${(e: Event) => handlers.setText(qKey, (e.target as HTMLInputElement).value)}
                    />`
                  : nothing}
              </div>
            `
          }
          return html`
            <button
              type="button"
              class="opt"
              data-checked=${selected ? 'true' : nothing}
              role=${role}
              aria-checked=${selected}
              aria-disabled=${disabled ? 'true' : nothing}
              ?disabled=${disabled}
              tabindex=${tabIndex}
              data-opt-id=${optId}
              @click=${() => handlers.toggleOption(qKey, oKey, multi)}
            >
              ${iconOption(multi, selected)}
              <span class="opt-text">${o.content}</span>
            </button>
          `
        })}
      </div>
    </div>
  `
  return { note: multi ? multiNote(limit, atCap) : undefined, answer }
}

// DROPDOWN 用自繪的 combobox + top-layer 面板（見 dropdown.ts 檔頭說明為何不用原生 <select>）。
// 這裡只負責 id 組裝，展開態的模板、樣式與鍵盤模型都在 dropdown.ts。
function dropdownQuestion(q: Question, state: QuestionState, handlers: QuestionHandlers, invalid: boolean): QuestionParts {
  return { answer: dropdownView({ q, token: qIdToken(q), errorId: qErrorId(q), state, handlers, invalid, sizing: state.sizing === true }) }
}

function scalarQuestion(q: Question, state: QuestionState, handlers: QuestionHandlers, invalid: boolean): QuestionParts {
  const qKey = String(q.id)
  const picked = state.optionAnswers[qKey] ?? []
  const anySelected = picked.length > 0
  const opts = q.options ?? []
  const answer = html`
    <div
      class="scale-row"
      role="radiogroup"
      aria-label=${q.text}
      aria-required=${q.is_required ? 'true' : nothing}
      aria-invalid=${invalid ? 'true' : nothing}
      aria-describedby=${invalid ? qErrorId(q) : nothing}
      @keydown=${(e: KeyboardEvent) => onRadioGroupKeydown(e, qKey, handlers)}
    >
      ${opts.map((o, i) => {
        const oKey = String(o.seq)
        const selected = picked.includes(oKey)
        // 按鈕數字由位置寫死（1..N），不讀 content；content 改當每一點的標籤（Figma：每點＝數字鈕＋標籤）。
        // 送出值仍走 o.seq（data-opt-id / toggleOption），與顯示數字解耦。
        return html`
          <div class="scale-item" data-checked=${selected ? 'true' : nothing}>
            <button
              type="button"
              class="scale-btn"
              role="radio"
              aria-checked=${selected}
              tabindex=${rovingTabindex(selected, i === 0, anySelected)}
              data-opt-id=${oKey}
              @click=${() => handlers.toggleOption(qKey, oKey, false)}
            >${i + 1}</button>
            ${o.content ? html`<span class="scale-label">${o.content}</span>` : nothing}
          </div>
        `
      })}
    </div>
  `
  return { answer }
}

// 自由填答的兩支：三種單行輸入題（text / email / phone）共用 freeTextQuestion，只差輸入型別
// 與缺省提示字；date 自己一支 —— 原生日期選擇器沒有長度概念，也不做輸入截斷。
// 兩支都渲染 Figma 的單行底線欄位，答案在送出時落進 text / date 欄位（見 buildSubmitResponses）。
// 兩支共用的 a11y 接法：aria-labelledby 接到 qTitle 的 heading id，避免題目文字要在兩處維護；
// is_required 同時反映到原生 required（AT 會自動讀出，即使沒有 <form> 包裹）與 aria-required；
// aria-describedby 只在標記為未通過時掛上 —— 指向的 .q-error 節點也只在那時才渲染。
function inputClass(invalid: boolean): string {
  return `line-input${invalid ? ' is-error' : ''}`
}

interface TextInput {
  /** <input type>。email / tel 讓行動裝置換上對應鍵盤。 */
  type: 'text' | 'email' | 'tel'
  /** inputmode；缺省不掛這個屬性。 */
  mode?: 'email' | 'tel'
  /** 題目自己沒帶 placeholder 時的缺省提示。 */
  placeholder: string
}

function freeTextQuestion(input: TextInput): QuestionBody {
  return (q, state, handlers, invalid) => {
    const qKey = String(q.id)
    // maxlength 是第一道限制；slice 防程式路徑（paste/IME edge cases）超長。
    const answer = html`
      <input
        class=${inputClass(invalid)}
        type=${input.type}
        inputmode=${input.mode ?? nothing}
        maxlength="500"
        placeholder=${q.placeholder ?? input.placeholder}
        aria-labelledby=${titleIdAttr(q, state)}
        ?required=${q.is_required}
        aria-required=${q.is_required ? 'true' : nothing}
        aria-invalid=${invalid ? 'true' : nothing}
        aria-describedby=${invalid ? qErrorId(q) : nothing}
        .value=${state.textAnswers[qKey] ?? ''}
        @input=${(e: Event) => handlers.setText(qKey, (e.target as HTMLInputElement).value.slice(0, 500))}
      />
    `
    return { answer }
  }
}

// 只問年份（或未來其他更窄語意）的 date 題：畫一個窄輸入框，不是日期選單 —— 選單會逼讀者
// 多挑兩段這題根本沒問的月日。值在送出前才補成完整日期（question-precision.ts 的 serialize）。
//
// type="text" + inputmode="numeric" 而不是 type="number"：number **不吃 maxlength**（HTML 規範
// 不把它套用在 number 上），第五個數字就擋不住；inputmode 一樣叫得出行動裝置的數字鍵盤。
// 非數字字元由 answerFormatValid 擋，跟範圍檢查同一關。
function narrowDateQuestion(
  q: Question,
  input: NonNullable<ReturnType<typeof precisionInput>>,
  state: QuestionState,
  handlers: QuestionHandlers,
  invalid: boolean,
): QuestionParts {
  const qKey = String(q.id)
  const answer = html`
    <input
      class=${inputClass(invalid)}
      type="text"
      inputmode="numeric"
      maxlength=${input.maxlength}
      placeholder=${q.placeholder ?? input.placeholder}
      aria-labelledby=${titleIdAttr(q, state)}
      ?required=${q.is_required}
      aria-required=${q.is_required ? 'true' : nothing}
      aria-invalid=${invalid ? 'true' : nothing}
      aria-describedby=${invalid ? qErrorId(q) : nothing}
      .value=${state.textAnswers[qKey] ?? ''}
      @input=${(e: Event) => handlers.setText(qKey, (e.target as HTMLInputElement).value.slice(0, input.maxlength))}
    />
  `
  return { answer }
}

function dateQuestion(q: Question, state: QuestionState, handlers: QuestionHandlers, invalid: boolean): QuestionParts {
  const qKey = String(q.id)
  const narrow = precisionInput(q)
  if (narrow !== undefined)
    return narrowDateQuestion(q, narrow, state, handlers, invalid)
  const answer = html`
    <input
      class=${inputClass(invalid)}
      type="date"
      placeholder=${q.placeholder ?? nothing}
      aria-labelledby=${titleIdAttr(q, state)}
      ?required=${q.is_required}
      aria-required=${q.is_required ? 'true' : nothing}
      aria-invalid=${invalid ? 'true' : nothing}
      aria-describedby=${invalid ? qErrorId(q) : nothing}
      .value=${state.textAnswers[qKey] ?? ''}
      @input=${(e: Event) => handlers.setText(qKey, (e.target as HTMLInputElement).value)}
    />
  `
  return { answer }
}

// 一題畫面上由題型決定的部分。標題與錯誤說明刻意不在這裡：兩者的位置（標題、錯誤、答案區依序）
// 由 questionView 這個唯一出口決定，題型的 body 連畫它們的機會都沒有 —— 新增題型時只要填這張表，
// 錯誤說明就一定在標題之下、答案之上，沒有哪一型可以漏放或放錯地方。
interface QuestionParts {
  /** 標題尾的題型註記（目前只有複選的「可複選…」）；沒有就不帶。 */
  note?: TemplateResult | undefined
  /** 標題與錯誤說明之後的答案區。 */
  answer: TemplateResult
}
type QuestionBody = (q: Question, state: QuestionState, handlers: QuestionHandlers, invalid: boolean) => QuestionParts

// 題型 → 畫面。單選與複選共用同一個選項渲染器（差別在指示器與可選數，由 isMultiSelect 決定）。
// Record<QuestionType, …> 是這裡的守門員：contract.ts 的聯集加一種而這張表沒跟上，type-check
// 就會擋下來。沒有它的話，查不到的題型會落到某個預設分支被畫成別的題型 —— 讀者看到一個
// 可以作答、但問錯東西的畫面，比整題不顯示更難察覺。
const QUESTION_BODIES: Record<QuestionType, QuestionBody> = {
  single_choice: optionQuestion,
  multiple_choice: optionQuestion,
  scalar: scalarQuestion,
  dropdown: dropdownQuestion,
  text: freeTextQuestion({ type: 'text', placeholder: '請輸入你的回答' }),
  email: freeTextQuestion({ type: 'email', mode: 'email', placeholder: 'you@example.com' }),
  phone: freeTextQuestion({ type: 'tel', mode: 'tel', placeholder: '例：0912345678' }),
  date: dateQuestion,
}

// 提示文字要說中真正的原因，而擋下作答的理由不一定是「沒填」：`answerGap` 對非空的文字答案
// 一律要求通過格式檢查，選填題也不例外。所以這裡不自己判斷缺什麼，只把 answerGap 判出的缺口
// 翻成一句話。表以 AnswerGap 為 key，state.ts 新增一種缺口時這張表會少一個 key、編不過；
// 沿用既有缺口名的新規則則咬不到，那種改動要自己回來看這句話還說不說得中。
const GAP_HINTS: Record<AnswerGap, (q: Question) => string> = {
  'required': () => '此題為必填',
  // 文案跟著擋下這題的那條規則走（question-type.ts 的 format 欄把 pattern 與話寫在一起；
  // 只問年份之類的窄題目改由 question-precision.ts 出規則與話），所以加一條規則就一定有
  // 一句對應的話，不會退回通用說法。與 answerGap 用的是同一支判斷。
  'text-format': q => answerFormatHint(q),
  'other-text-missing': () => '請填寫「其他」的內容',
  // 後台填了自訂訊息就用它，否則依 mode 用預設句。標題旁的 q-note 是另一套文案（描述規則而非
  // 要求補救），兩者刻意不共用字串。
  'selection-limit': q => selectionLimitHint(resolveSelectionLimit(q)),
}

function selectionLimitHint(limit: SelectionLimit | null): string {
  // answerGap 只在有限制時才回這個缺口，null 到不了這裡；留一句通用說法讓型別窮盡。
  if (limit === null)
    return '選取的項目數量不符'
  if (limit.message !== undefined)
    return limit.message
  switch (limit.mode) {
    case 'at_least': return `請至少選擇 ${limit.count} 項`
    case 'at_most': return `最多只能選擇 ${limit.count} 項`
    case 'exactly': return `請選擇 ${limit.count} 項`
  }
}

// 標記是按下推進鈕當下留下的，作答會撤掉它（survey-element 的 clearHints），所以走使用者
// 輸入這條路進來時必然還有缺口。不經輸入而改動答案的路徑就不一定：零作答被標紅之後，晚到的
// mpid 觸發重新載入同一份問卷，prefill seed 會在標記還在時把答案補進去。那時已經沒有缺口可
// 報，退回最通用的說法而不是留一段空白。
function invalidHint(step: Step, state: QuestionState): string {
  const gap = answerGap(step, state.optionAnswers, state.textAnswers)
  return gap === undefined ? '此題為必填' : GAP_HINTS[gap](step.q)
}

export function questionView(step: Step, state: QuestionState, handlers: QuestionHandlers): TemplateResult {
  const q = step.q
  const invalid = (state.invalidQIds ?? []).includes(String(q.id))
  const { note, answer } = QUESTION_BODIES[q.type](q, state, handlers, invalid)
  // 錯誤說明緊接在標題之下、答案區之上：若排在答案之後，選項多到要在作答區內捲動時說明會落在
  // 捲動區之外，按下推進鈕被擋下的讀者看不到原因。各控制項以 aria-describedby 指回來，順序不影響 AT。
  return html`
    ${qTitle(q, state, note)}
    ${invalid ? html`<p class="q-error" id=${qErrorId(q)}>${invalidHint(step, state)}</p>` : nothing}
    ${answer}
  `
}
